import React from 'react';
import { Platform } from 'react-native';
import Svg, { Circle, Path, Rect } from 'react-native-svg';

/**
 * Значки списка чатов и экрана «Новое сообщение» (аудит удобства 2026-10-04).
 *
 * <p>Рисованные и одноцветные по той же причине, что и {@link ./BellGlyph}: все значки шапки
 * тонируются одним цветом темы, а картинка-эмодзи выпадает из ряда. Видеозвонок здесь —
 * камера, а не глаз (`eye.png`): глаз читался как «посмотреть», и человек не находил видео.
 */

type GlyphProps = { size?: number; color: string; strokeWidth?: number };

const hidden = Platform.OS === 'web' ? {} : { accessibilityElementsHidden: true };

function stroke(color: string, width: number) {
  return {
    fill: 'none',
    stroke: color,
    strokeWidth: width,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
}

/** Видеокамера — видеозвонок. */
export function VideoCameraGlyph({ size = 22, color, strokeWidth = 1.8 }: GlyphProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" {...hidden}>
      <Rect x={2.8} y={6.4} width={12.6} height={11.2} rx={2.4} {...stroke(color, strokeWidth)} />
      <Path d="m15.4 10.4 5-2.9a.6.6 0 0 1 .9.52v7.96a.6.6 0 0 1-.9.52l-5-2.9" {...stroke(color, strokeWidth)} />
    </Svg>
  );
}

/** Карандаш над листом — «новое сообщение» на iOS и в вебе. */
export function ComposeGlyph({ size = 22, color, strokeWidth = 1.8 }: GlyphProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" {...hidden}>
      <Path d="M11 4.5H6.2A2.2 2.2 0 0 0 4 6.7v11.1A2.2 2.2 0 0 0 6.2 20h11.1a2.2 2.2 0 0 0 2.2-2.2V13" {...stroke(color, strokeWidth)} />
      <Path d="M17.6 3.6a1.9 1.9 0 0 1 2.7 2.7l-7.6 7.6-3.5.8.8-3.5 7.6-7.6Z" {...stroke(color, strokeWidth)} />
    </Svg>
  );
}

/** Плюс — плавающая кнопка на Android. */
export function PlusGlyph({ size = 26, color, strokeWidth = 2.4 }: GlyphProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" {...hidden}>
      <Path d="M12 5v14M5 12h14" {...stroke(color, strokeWidth)} />
    </Svg>
  );
}

/**
 * Щит с ключом — вход в управление пользователями (BOSS). Свой значок, а не «люди и чаты»:
 * та же картинка стояла на кнопке «новая группа», и администратор путал две кнопки (п. P2).
 */
export function AdminShieldGlyph({ size = 22, color, strokeWidth = 1.8 }: GlyphProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" {...hidden}>
      <Path d="M12 3 5 5.8v5.4c0 4.4 3 8.2 7 9.8 4-1.6 7-5.4 7-9.8V5.8L12 3Z" {...stroke(color, strokeWidth)} />
      <Circle cx={12} cy={10.6} r={2} {...stroke(color, strokeWidth)} />
      <Path d="M12 12.6v3.4M12 14.6h1.6" {...stroke(color, strokeWidth)} />
    </Svg>
  );
}

/**
 * Видеокамера — встречи по ссылке (заказ владельца 2026-10-12). Камера, а не трубка: трубка в
 * шапке читалась бы как «позвонить», а здесь — список постоянных комнат для созвонов.
 */
export function MeetingGlyph({ size = 22, color, strokeWidth = 1.8 }: GlyphProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" {...hidden}>
      <Rect x={3} y={6.5} width={12.5} height={11} rx={2.5} {...stroke(color, strokeWidth)} />
      <Path d="m15.5 10.6 5-2.9v8.6l-5-2.9" {...stroke(color, strokeWidth)} />
    </Svg>
  );
}

/** Лупа — поле поиска. */
export function SearchGlyph({ size = 18, color, strokeWidth = 2 }: GlyphProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" {...hidden}>
      <Circle cx={10.5} cy={10.5} r={6} {...stroke(color, strokeWidth)} />
      <Path d="m15 15 5 5" {...stroke(color, strokeWidth)} />
    </Svg>
  );
}

/** Крестик — отклонить заявку, очистить поле, убрать участника. */
export function CrossGlyph({ size = 18, color, strokeWidth = 2.2 }: GlyphProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" {...hidden}>
      <Path d="M6 6l12 12M18 6 6 18" {...stroke(color, strokeWidth)} />
    </Svg>
  );
}

/** Галочка — выбранный участник новой группы. */
export function CheckGlyph({ size = 16, color, strokeWidth = 2.6 }: GlyphProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" {...hidden}>
      <Path d="m5 12.5 4.5 4.5L19 7.5" {...stroke(color, strokeWidth)} />
    </Svg>
  );
}

/** Перечёркнутый колокольчик — заглушённый чат в строке списка. */
export function MutedBellGlyph({ size = 15, color, strokeWidth = 2 }: GlyphProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" {...hidden}>
      <Path
        d="M12 3.2a5.4 5.4 0 0 0-5.4 5.4v2.7c0 .9-.36 1.77-1 2.4l-.8.8a.9.9 0 0 0 .64 1.54h13.12a.9.9 0 0 0 .64-1.54l-.8-.8a3.4 3.4 0 0 1-1-2.4V8.6A5.4 5.4 0 0 0 12 3.2Z"
        {...stroke(color, strokeWidth)}
      />
      <Path d="M10.2 18.4a1.85 1.85 0 0 0 3.6 0M4 3l16 18" {...stroke(color, strokeWidth)} />
    </Svg>
  );
}

/** Значок каталога каналов — рупор. Тот же, что стоял на прежней кнопке шапки. */
export function MegaphoneGlyph({ size = 22, color, strokeWidth = 1.9 }: GlyphProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" {...hidden}>
      <Path
        d="M3 10.2a1 1 0 0 1 1-1h2.7l7.7-4.2A1 1 0 0 1 16 5.9v12.2a1 1 0 0 1-1.6.8l-7.7-4.2H4a1 1 0 0 1-1-1v-3.5Z"
        {...stroke(color, strokeWidth)}
      />
      <Path d="M8 14.5V19a1 1 0 0 0 1 1h1.2a1 1 0 0 0 .95-1.32l-1.15-3.48" {...stroke(color, strokeWidth)} />
      <Path d="M18.2 8.4a4.8 4.8 0 0 1 0 7.2M20.6 6a8 8 0 0 1 0 12" {...stroke(color, strokeWidth)} />
    </Svg>
  );
}

/** Люди — «новая группа». */
export function GroupGlyph({ size = 22, color, strokeWidth = 1.8 }: GlyphProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" {...hidden}>
      <Circle cx={9} cy={8.5} r={3.2} {...stroke(color, strokeWidth)} />
      <Path d="M3.2 19c.6-3.1 3-5 5.8-5s5.2 1.9 5.8 5" {...stroke(color, strokeWidth)} />
      <Path d="M15.5 5.6a3 3 0 0 1 0 5.8M17.6 14.3c1.7.6 2.9 2.2 3.2 4.7" {...stroke(color, strokeWidth)} />
    </Svg>
  );
}

/** Человек с плюсом — «добавить контакт». */
export function AddPersonGlyph({ size = 22, color, strokeWidth = 1.8 }: GlyphProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" {...hidden}>
      <Circle cx={10} cy={8.5} r={3.4} {...stroke(color, strokeWidth)} />
      <Path d="M3.6 19.4c.7-3.3 3.3-5.3 6.4-5.3 1.4 0 2.7.4 3.8 1.2M18.5 13v6M15.5 16h6" {...stroke(color, strokeWidth)} />
    </Svg>
  );
}

/** QR-квадраты — «пригласить по ссылке / QR». */
export function QrGlyph({ size = 22, color, strokeWidth = 1.8 }: GlyphProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" {...hidden}>
      <Rect x={3.5} y={3.5} width={6.5} height={6.5} rx={1.2} {...stroke(color, strokeWidth)} />
      <Rect x={14} y={3.5} width={6.5} height={6.5} rx={1.2} {...stroke(color, strokeWidth)} />
      <Rect x={3.5} y={14} width={6.5} height={6.5} rx={1.2} {...stroke(color, strokeWidth)} />
      <Path d="M14 14h2.6v2.6H14zM18 18h2.5v2.5H18zM14 19.5h1.5M19.5 14v1.5" {...stroke(color, strokeWidth)} />
    </Svg>
  );
}

/** Три точки — «ещё» в карточке профиля. */
export function MoreGlyph({ size = 22, color }: Omit<GlyphProps, 'strokeWidth'>) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" {...hidden}>
      <Circle cx={5.5} cy={12} r={1.7} fill={color} />
      <Circle cx={12} cy={12} r={1.7} fill={color} />
      <Circle cx={18.5} cy={12} r={1.7} fill={color} />
    </Svg>
  );
}
